/* eslint-disable prettier/prettier */
import { Button, Grid, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import Container from '../../components/Container'
import TemplateRenderer from '../../components/TemplateRenderer'
import {
    EMPTY_RECIPIENT,
    TestRecipientFields,
    TestSendStatus,
    TestVariableFields,
} from '../../components/TestSendFields'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { clearTemplateData, putCommType } from '../../features/templateSlice'
import {
    buildTestPayload,
    testRecipient,
    variablesToAsk,
} from '../../helpers/testSend'
import useTestSend from '../../hooks/useTestSend'
import { errorMessage } from '../../services/axios/errorMessage'
import TemplateService from '../../services/templateService'
import { nowUnix } from '../../utils/datetime'
import { RADIUS, SURFACE, TEXT } from '../../theme/tokens'

// The polling cadence lives with the shared test-send hook.
export { MAX_POLL_ATTEMPTS, POLL_INTERVAL_MS } from '../../hooks/useTestSend'

// The theme's outlined button (hairline, secondary label, orange edge and
// tint on hover); a disabled one keeps its label readable.
const outlinedBtnSx = {
    fontWeight: 600,
    '&.Mui-disabled': {
        color: TEXT.disabled,
        borderColor: SURFACE.border,
    },
}

// Colours come from the theme's contained primary button (Deep Blue on
// orange, AA); only layout here.
const containedBtnSx = {
    fontWeight: 700,
    boxShadow: 'none',
}

const TestTemplate = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    // Name, email and CC, Slack target, device token, or mobile number.
    const [recipientValues, setRecipientValues] = useState(EMPTY_RECIPIENT)
    // Whether the template loaded: loading | ready | error (with the reason).
    // A failed load used to leave a blank preview and Send enabled.
    const [load, setLoad] = useState({ state: 'loading', reason: '' })
    // idle | sending | pending | delivered | failed | error | timeout
    const { sendState, statusMessage, busy, send } = useTestSend()
    const [approvalError, setApprovalError] = useState('')
    const [approving, setApproving] = useState(false)
    // The template row (subject, declared variables, body), for the variables
    // a test must supply; null until loaded, or if it could not be.
    const [templateRow, setTemplateRow] = useState(null)
    const [variableValues, setVariableValues] = useState({})
    const { template_id, comm_type } = useParams()
    const previewBody = useSelector((state) => state.template.templateData)

    /**
     * Load the preview and the template row. Without the preview there is
     * nothing to test: a load error with the reason (the row's when it has
     * one, e.g. "Template not found"). Without only the row, the preview body
     * still yields the variables; only subject-only ones would be missed.
     */
    const loadTemplate = async () => {
        setLoad({ state: 'loading', reason: '' })
        const [preview, row] = await Promise.all([
            dispatch(TemplateService.getTemplateData(template_id)),
            dispatch(TemplateService.getTemplate(template_id)),
        ])
        if (!row.error) setTemplateRow(row.payload?.data?.template ?? null)
        if (preview.error) {
            setLoad({
                state: 'error',
                reason: errorMessage({
                    data: row.error ? row.payload : preview.payload,
                }),
            })
            return
        }
        setLoad({ state: 'ready', reason: '' })
    }

    useEffect(() => {
        if (comm_type && template_id) {
            dispatch(putCommType(comm_type))
            loadTemplate()
        }
    }, [])

    const variables = variablesToAsk({
        commType: comm_type,
        texts: [
            templateRow?.subject,
            templateRow?.extracted_body,
            templateRow?.template_data,
            typeof previewBody === 'string' ? previewBody : '',
        ],
        declared: templateRow?.others,
    })
    const missingVariables = variables.filter(
        (v) => !String(variableValues[v] ?? '').trim()
    )

    const recipient = testRecipient(comm_type, recipientValues)

    const onCommunicationSendClick = () =>
        send(
            buildTestPayload({
                templateId: template_id,
                commType: comm_type,
                name: recipientValues.name,
                email: recipientValues.email,
                ccs: recipient.ccs,
                mobile: recipient.mobile,
                receiver: recipient.receiver,
                others: Object.fromEntries(
                    variables.map((v) => [v, variableValues[v] ?? ''])
                ),
                eventId: uuidv4(),
                eventTime: nowUnix(),
                // A WhatsApp test goes out from the template's own account.
                channel:
                    comm_type === 'whatsapp' ? templateRow?.channel ?? '' : '',
            })
        )

    const onApproveTemplateClick = () => {
        setApprovalError('')
        setApproving(true)
        dispatch(TemplateService.sendForApproval(template_id)).then((resp) => {
            setApproving(false)
            if (resp.error) {
                setApprovalError(
                    `Not sent for approval: ${errorMessage({
                        data: resp.payload,
                    })}`
                )
                return
            }
            dispatch(clearTemplateData())
            navigate('/templates')
        })
    }

    const isSendDisabled = () =>
        busy ||
        load.state !== 'ready' ||
        missingVariables.length > 0 ||
        !recipient.ready
    // Approval only after a send that was delivered, or whose outcome could
    // not be confirmed in time (the same rule v1 used).
    const canSendForApproval =
        (sendState === 'delivered' || sendState === 'timeout') && !approving

    return (
        <Container title="Test template">
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <Box
                        sx={{
                            height: '90vh',
                            border: `1px solid ${SURFACE.border}`,
                            borderRadius: `${RADIUS.md}px`,
                            overflow: 'hidden',
                            // The template renders on an email client's
                            // white in both themes.
                            background: SURFACE.emailCanvas,
                        }}
                    >
                        <TemplateRenderer />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Grid container direction="column" spacing={2}>
                        {load.state === 'error' && (
                            <Grid item>
                                <ErrorBanner
                                    message={`The template could not be loaded, so it cannot be tested: ${load.reason}`}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={loadTemplate}
                                    sx={{ ...outlinedBtnSx, mt: 1.5 }}
                                >
                                    Retry
                                </Button>
                            </Grid>
                        )}
                        {load.state === 'loading' && (
                            <Grid item>
                                <Typography
                                    role="status"
                                    variant="body2"
                                    sx={{ color: TEXT.muted }}
                                >
                                    Loading the template…
                                </Typography>
                            </Grid>
                        )}
                        <TestRecipientFields
                            commType={comm_type}
                            values={recipientValues}
                            onChange={(patch) =>
                                setRecipientValues((prev) => ({
                                    ...prev,
                                    ...patch,
                                }))
                            }
                        />

                        {variables.length > 0 && (
                            <Grid item>
                                <TestVariableFields
                                    variables={variables}
                                    values={variableValues}
                                    onChange={setVariableValues}
                                />
                            </Grid>
                        )}

                        {statusMessage && (
                            <Grid item>
                                <TestSendStatus
                                    sendState={sendState}
                                    statusMessage={statusMessage}
                                    busy={busy}
                                />
                            </Grid>
                        )}

                        {approvalError && (
                            <Grid item>
                                <ErrorBanner message={approvalError} />
                            </Grid>
                        )}

                        <Grid item>
                            <Button
                                variant="outlined"
                                onClick={onCommunicationSendClick}
                                disabled={isSendDisabled()}
                                sx={outlinedBtnSx}
                            >
                                Send test communication
                            </Button>
                            <Button
                                variant="contained"
                                onClick={onApproveTemplateClick}
                                disabled={!canSendForApproval}
                                sx={{ ...containedBtnSx, ml: 3 }}
                            >
                                Send for Approval
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

export default TestTemplate
